import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Inbox, LifeBuoy, Building2, Mail, ArrowLeft, Moon, Sun } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CONTACT_EMAIL = "contact@elvarify.com";

export default function Contact() {
  const { t } = useTranslation();
  const { dark, toggle } = useTheme();

  const cards = [
    {
      icon: LifeBuoy, title: t("contact.supportTitle"), desc: t("contact.supportDesc"),
      subject: "Support Elvarify", testid: "support",
    },
    {
      icon: Building2, title: t("contact.customTitle"), desc: t("contact.customDesc"),
      subject: "Offre sur mesure", testid: "custom",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="contact-page">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
            <div className="h-8 w-8 rounded-lg bg-primary dark:bg-electric flex items-center justify-center">
              <Inbox size={18} className="text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">{t("common.appName")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={toggle} data-testid="contact-theme-toggle" className="p-2 rounded-full hover:bg-secondary transition-colors">
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-0 h-[400px] w-[400px] rounded-full bg-electric/8 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-primary/8 blur-3xl" />
        </div>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-center max-w-2xl relative"
          data-testid="contact-title">
          {t("contact.title")}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
          className="mt-5 text-muted-foreground text-sm sm:text-base text-center max-w-xl relative" data-testid="contact-sub">
          {t("contact.sub")}
        </motion.p>

        <div className="mt-16 grid sm:grid-cols-2 gap-6 w-full max-w-3xl relative">
          {cards.map((c, i) => (
            <motion.div key={c.testid}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl border border-border bg-card p-8 card-hover flex flex-col"
              data-testid={`contact-${c.testid}-card`}>
              <div className="h-11 w-11 rounded-xl bg-electric/10 flex items-center justify-center mb-6">
                <c.icon size={20} className="text-electric" />
              </div>
              <h2 className="font-display font-bold text-lg mb-2">{c.title}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8 flex-1">{c.desc}</p>
              <p className="text-[11px] text-muted-foreground mb-2">
                {t("contact.emailLabel")}{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid={`contact-email-${c.testid}`}
                  className="text-electric font-semibold hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(c.subject)}`}
                data-testid={`contact-mailto-${c.testid}`}
                className="w-full rounded-full bg-electric text-white py-3 text-sm font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                <Mail size={15} /> {t("contact.cta")}
              </a>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="mt-14 relative">
          <Link to="/" data-testid="contact-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={15} /> {t("contact.back")}
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
